import { FC, useState } from 'react';
import s from './ContextMenu.module.css';

import { ScrollableContainer } from '../ScrollableContainer';
import { WarningModal, WarningModalProps } from '../WarningModal/WarningModal';

type ContextMenuProps = {
  ref?: React.RefObject<HTMLDivElement>;
  style?: React.CSSProperties;
  className?: string;
  options: ContextMenuOption[];
  onClose?: () => void;
};

type ContextMenuOptionBase = {
  id: number | string;
  children?: React.ReactNode;
};

type ContextMenuConfirmation = Pick<
  WarningModalProps,
  'title' | 'message' | 'confirmLabel'
>;

type ImmediateContextMenuOption = ContextMenuOptionBase & {
  onClick?: (e: React.MouseEvent) => void;
  confirmation?: never;
};

type ConfirmableContextMenuOption = ContextMenuOptionBase & {
  onClick: () => void;
  confirmation: ContextMenuConfirmation;
};

export type ContextMenuOption =
  | ImmediateContextMenuOption
  | ConfirmableContextMenuOption;

export const ContextMenu: FC<ContextMenuProps> = ({
  ref,
  className = '',
  style = {},
  options,
  onClose,
}) => {
  const [pendingOption, setPendingOption] =
    useState<ConfirmableContextMenuOption>();

  const handleConfirm = () => {
    const onClick = pendingOption?.onClick;
    setPendingOption(undefined);
    onClick?.();
  };

  const handleCloseConfirmation = () => {
    setPendingOption(undefined);
    onClose?.();
  };

  return (
    <>
      <ScrollableContainer ref={ref} className={`${s.scroll} ${className}`}>
        <ul style={style} className={`${s.menu}`}>
          {options.map((option) => (
            <li
              key={option.id}
              onClick={(e) => {
                e.stopPropagation();

                if (option.confirmation) {
                  setPendingOption(option);
                  return;
                }

                option.onClick?.(e);
              }}
            >
              {option.children}
            </li>
          ))}
        </ul>
      </ScrollableContainer>

      {pendingOption && (
        <WarningModal
          {...pendingOption.confirmation}
          onClose={handleCloseConfirmation}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};
